document.addEventListener('DOMContentLoaded', function() {
  'use strict';

  const roots = Array.from(document.querySelectorAll('.js-autocomplete'));
  if (roots.length === 0) return;

  const states = roots.map(initAutocomplete).filter(Boolean);
  if (states.length === 0) return;

  document.addEventListener('click', function(event) {
    states.forEach(state => {
      if (!state.root.contains(event.target)) {
        closeResults(state);
      }
    });
  });

  function initAutocomplete(root) {
    const input = root.querySelector('.js-autocomplete-input');
    const resultsContainer = root.querySelector('.js-autocomplete-results');
    if (!input || !resultsContainer) return null;

    const state = {
      root: root,
      input: input,
      resultsContainer: resultsContainer,
      results: [],
      selectedIndex: -1,
      debounceId: null,
      requestId: 0,
      lastQuery: ''
    };

    input.addEventListener('input', function() {
      clearTimeout(state.debounceId);
      state.debounceId = setTimeout(function() {
        const query = input.value.trim();
        if (query.length < 2) {
          closeResults(state);
          return;
        }
        fetchResults(state, query, true);
      }, 200);
    });

    input.addEventListener('keydown', function(event) {
      const key = event.key;
      if (key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(state, 1);
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(state, -1);
        return;
      }

      if (key === 'Escape') {
        closeResults(state);
        return;
      }

      if (key === 'Enter') {
        event.preventDefault();
        handleEnter(state);
      }
    });

    resultsContainer.addEventListener('mousedown', function(event) {
      const link = event.target.closest('.autocomplete-link');
      if (!link) return;
      const url = link.getAttribute('href');
      if (!url || !isSafeUrl(url)) return;
      window.location.href = url;
    });

    return state;
  }

  function moveSelection(state, direction) {
    if (!state.results || state.results.length === 0) return;

    const maxIndex = state.results.length - 1;
    let nextIndex = state.selectedIndex + direction;

    if (nextIndex > maxIndex) nextIndex = -1;
    if (nextIndex < -1) nextIndex = maxIndex;

    state.selectedIndex = nextIndex;
    renderResults(state, state.lastQuery);
  }

  function handleEnter(state) {
    const query = state.input.value.trim();
    if (query.length < 2) return;

    if (state.selectedIndex >= 0 && state.results[state.selectedIndex]) {
      var selectedUrl = state.results[state.selectedIndex].url;
      if (isSafeUrl(selectedUrl)) window.location.href = selectedUrl;
      return;
    }

    const resolve = function(results) {
      const exact = findExactMatch(results, query);
      if (exact && isSafeUrl(exact.url)) {
        window.location.href = exact.url;
        return;
      }
      const searchUrl = state.root.getAttribute('data-search-url') || state.input.getAttribute('data-search-url');
      if (searchUrl && isSafeUrl(searchUrl)) {
        window.location.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
      }
    };

    if (state.lastQuery === query && state.results.length > 0) {
      resolve(state.results);
      return;
    }

    fetchResults(state, query, false).then(resolve);
  }

  function fetchResults(state, query, shouldRender) {
    const baseUrl = state.root.getAttribute('data-autocomplete-url') ||
      state.input.getAttribute('data-autocomplete-url') ||
      state.input.getAttribute('data-url');

    if (!baseUrl) return Promise.resolve([]);

    state.requestId += 1;
    const requestId = state.requestId;

    return fetch(`${baseUrl}?q=${encodeURIComponent(query)}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(response => response.ok ? response.json() : [])
      .then(data => {
        if (requestId !== state.requestId) return [];
        const results = Array.isArray(data) ? data : [];
        state.results = results;
        state.lastQuery = query;
        state.selectedIndex = -1;
        if (shouldRender) renderResults(state, query);
        return results;
      })
      .catch(function() {
        if (requestId !== state.requestId) return [];
        state.results = [];
        state.lastQuery = query;
        closeResults(state);
        return [];
      });
  }

  function renderResults(state, query) {
    if (!state.results || state.results.length === 0) {
      state.resultsContainer.innerHTML = `
        <div class="autocomplete-dropdown">
          <div class="autocomplete-empty">No results found</div>
        </div>
      `;
      return;
    }

    let previousSection = null;
    const items = state.results.map(function(row, index) {
      const section = sectionForType(row.type);
      const sectionTitle = sectionTitleFor(section);
      const label = highlightText(row.label || '', query);
      const subtitle = row.subtitle ? highlightText(row.subtitle, query) : '';
      const itemType = itemTypeLabelFor(row.type);
      const isActive = index === state.selectedIndex ? ' is-active' : '';
      const sectionHeader = section !== previousSection
        ? `<div class="autocomplete-section-title">${sectionTitle}</div>`
        : '';
      previousSection = section;

      const safeUrl = isSafeUrl(row.url) ? escapeHtml(row.url) : '#';

      return `
        ${sectionHeader}
        <a href="${safeUrl}" class="autocomplete-link${isActive}" data-autocomplete-index="${index}">
          <div class="autocomplete-item">
            <div class="autocomplete-row">
              <div class="autocomplete-label">${label}</div>
              ${itemType ? `<div class="autocomplete-type-pill">${itemType}</div>` : ''}
            </div>
            ${subtitle ? `<div class="autocomplete-subtitle">${subtitle}</div>` : ''}
          </div>
        </a>
      `;
    }).join('');

    state.resultsContainer.innerHTML = `
      <div class="autocomplete-dropdown" role="listbox">
        ${items}
      </div>
    `;
  }

  function closeResults(state) {
    state.resultsContainer.innerHTML = '';
    state.results = [];
    state.selectedIndex = -1;
  }

  function findExactMatch(results, query) {
    const queryUpper = query.toUpperCase();

    return results.find(function(row) {
      if (!row || !row.type) return false;

      if (row.type === 'ticker') {
        const symbol = extractLeadingToken(row.label);
        return symbol === queryUpper;
      }

      if (row.type === 'manager') {
        const cik = String(row.cik || '').toUpperCase();
        return cik === queryUpper;
      }

      if (row.type === 'cusip') {
        const cusip = String(row.cusip || '').toUpperCase() || extractLeadingToken(row.label);
        return cusip === queryUpper;
      }

      return false;
    });
  }

  function sectionForType(type) {
    if (type === 'manager') return 'managers';
    if (type === 'ticker' || type === 'company' || type === 'cusip') return 'investments';
    return 'results';
  }

  function sectionTitleFor(section) {
    switch (section) {
    case 'managers':
      return 'Managers';
    case 'investments':
      return 'Investments';
    default:
      return 'Results';
    }
  }

  function itemTypeLabelFor(type) {
    switch (type) {
    case 'ticker':
      return '';
    case 'company':
      return '';
    case 'cusip':
      return '';
    default:
      return '';
    }
  }

  function extractLeadingToken(label) {
    if (!label) return '';
    return label.split(' — ')[0].trim().toUpperCase();
  }

  function highlightText(text, query) {
    if (!query) return escapeHtml(text);

    const escaped = escapeHtml(text);
    const pattern = new RegExp(escapeRegExp(query), 'ig');
    return escaped.replace(pattern, function(match) {
      return `<mark class="search-highlight">${match}</mark>`;
    });
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isSafeUrl(url) {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('/') && !url.startsWith('//')) return true;
    try {
      var parsed = new URL(url, window.location.href);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }
});
